import { useState, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import EventCard from "@/components/EventCard";
import { type Event as StaticEvent } from "@/data/events";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Calendar as CalendarIcon, List, MapPin, AlertCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type DBEvent = Tables<"events">;

// Convert database event to static event format
// Parse YYYY-MM-DD as local date (not UTC) to avoid off-by-one day shifts
const parseLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Format 24h time (e.g. "14:00") to 12h (e.g. "2:00 PM")
const formatTime12h = (time: string): string => {
  const date = new Date(`2000-01-01T${time}`);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const convertDBEventToEvent = (dbEvent: DBEvent): StaticEvent => ({
  id: dbEvent.id,
  dbEventId: dbEvent.id,
  title: dbEvent.title,
  type: dbEvent.is_special ? "special" : "weekly",
  date: parseLocalDate(dbEvent.date),
  startTime: formatTime12h(dbEvent.start_time),
  endTime: formatTime12h(dbEvent.end_time),
  location: dbEvent.location,
  description: dbEvent.description,
  externalLink: dbEvent.external_link || undefined,
  imageUrl: dbEvent.image_url || undefined,
  isRecurring: !dbEvent.is_special,
  recurrence: !dbEvent.is_special ? dbEvent.type : undefined,
  chapter: dbEvent.chapter as "washington" | "union" | "all",
});

const Events = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const deepLinkEventId = searchParams.get("event") || "";
  const deepLinkDate = searchParams.get("date") || "";
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [dbEvents, setDbEvents] = useState<DBEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deepLinkedEventId, setDeepLinkedEventId] = useState<string | null>(null);
  const [autoOpenDetailsId, setAutoOpenDetailsId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const isMobile = useIsMobile();
  const handledDeepLinkRef = useRef(false);

  // Determine chapter based on URL path (e.g., /washington/events or /union/events)
  const pathSegments = location.pathname.split('/');
  const chapterSlug = pathSegments[1]; // 'washington' or 'union'
  const isUnionChapter = chapterSlug === 'union';
  const chapterFilter = isUnionChapter ? 'union' : 'washington';
  const chapterName = isUnionChapter ? 'Union' : 'Washington';

  useEffect(() => {
    fetchEvents();

    // Set up real-time subscription
    const channel = supabase
      .channel('events-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events'
        },
        () => {
          fetchEvents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chapterFilter]);

  useEffect(() => {
    setPage(1);
  }, [chapterFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch events for this chapter OR shared events (chapter = 'all')
      const { data, error: fetchError } = await supabase
        .from('events')
        .select('*')
        .or(`chapter.eq.${chapterFilter},chapter.eq.all`)
        .order('date');

      if (fetchError) throw fetchError;
      setDbEvents(data || []);
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err?.message ? String(err.message) : String(err));
      setDbEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Expand recurring events into individual dated occurrences
  const expandRecurringEvents = (events: DBEvent[]): StaticEvent[] => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear() + 1, 11, 31);
    const out: StaticEvent[] = [];

    for (const e of events) {
      if (e.is_special) {
        out.push(convertDBEventToEvent(e));
        continue;
      }

      if (e.type === "2nd & 4th Wednesday") {
        let year = now.getFullYear();
        let month = now.getMonth();
        while (year < end.getFullYear() || (year === end.getFullYear() && month <= end.getMonth())) {
          // Collect Wednesdays of this month, take the 2nd and 4th
          const wednesdays: Date[] = [];
          const d = new Date(year, month, 1);
          while (d.getDay() !== 3) d.setDate(d.getDate() + 1);
          while (d.getMonth() === month) {
            wednesdays.push(new Date(d));
            d.setDate(d.getDate() + 7);
          }
          for (const idx of [1, 3]) {
            const occ = wednesdays[idx];
            if (!occ || occ < now) continue;
            const dateStr = `${occ.getFullYear()}-${String(occ.getMonth() + 1).padStart(2, '0')}-${String(occ.getDate()).padStart(2, '0')}`;
            out.push({
              ...convertDBEventToEvent({ ...e, date: dateStr }),
              id: `${e.id}-${dateStr}`,
              isRecurring: true,
              recurrence: e.type,
            });
          }
          month++;
          if (month > 11) { month = 0; year++; }
        }
      } else if (e.type === "Last Wednesday of Month") {
        let year = now.getFullYear();
        let month = now.getMonth();
        while (year < end.getFullYear() || (year === end.getFullYear() && month <= end.getMonth())) {
          // Last day of month, then walk back to Wednesday
          const lastDay = new Date(year, month + 1, 0);
          while (lastDay.getDay() !== 3) lastDay.setDate(lastDay.getDate() - 1);
          if (lastDay >= now) {
            const dateStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
            out.push({
              ...convertDBEventToEvent({ ...e, date: dateStr }),
              id: `${e.id}-${dateStr}`,
              isRecurring: true,
              recurrence: e.type,
            });
          }
          month++;
          if (month > 11) { month = 0; year++; }
        }
      } else {

        // Default: treat as weekly recurring (every 7 days from the event's weekday)
        const startDay = parseLocalDate(e.date).getDay();
        const d = new Date(now);
        while (d.getDay() !== startDay) d.setDate(d.getDate() + 1);
        while (d <= end) {
          const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          out.push({
            ...convertDBEventToEvent({ ...e, date: dateStr }),
            id: `${e.id}-${dateStr}`,
            isRecurring: true,
            recurrence: e.type,
          });
          d.setDate(d.getDate() + 7);
        }
      }
    }

    return out.sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  let allEvents: StaticEvent[] = [];
  try {
    allEvents = expandRecurringEvents(dbEvents);
  } catch (err: any) {
    console.error('Error expanding events:', err);
    if (!error) {
      // Surface expansion errors via the same error UI on next render
      // (using setState here would loop; instead we just log and show empty)
    }
  }

  // Honor deep-link ?event=<dbEventId>&date=<YYYY-MM-DD>
  useEffect(() => {
    if (handledDeepLinkRef.current) return;
    if (loading) return;
    if (!deepLinkEventId) return;
    const match = allEvents.find(
      (e) =>
        e.dbEventId === deepLinkEventId &&
        (!deepLinkDate ||
          `${e.date.getFullYear()}-${String(e.date.getMonth() + 1).padStart(2, "0")}-${String(e.date.getDate()).padStart(2, "0")}` === deepLinkDate),
    );
    if (!match) return;
    handledDeepLinkRef.current = true;
    setSelectedDate(match.date);
    setDeepLinkedEventId(match.id);
    if (match.type === "special") setAutoOpenDetailsId(match.id);
    // Scroll into view shortly after render
    setTimeout(() => {
      const el = document.getElementById(`event-${match.id}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [loading, dbEvents, deepLinkEventId, deepLinkDate]);

  const renderStatusCard = () => {
    if (loading) {
      return (
        <Card className="p-12 text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 text-muted-foreground animate-spin" />
          <p className="text-muted-foreground">Loading events…</p>
        </Card>
      );
    }
    if (error) {
      return (
        <Card className="p-8 text-center border-destructive/40 bg-destructive/5">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <h3 className="text-lg font-semibold mb-2">Couldn't load events</h3>
          <p className="text-sm text-muted-foreground mb-4 break-words">{error}</p>
          <Button variant="outline" onClick={fetchEvents}>Retry</Button>
        </Card>
      );
    }
    return null;
  };


  // For list view: paginated upcoming events. If a deep-linked event exists on page 1, ensure it's included.
  const getListViewEvents = (): StaticEvent[] => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const upcoming = allEvents.filter(event => event.date >= now);
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const base = upcoming.slice(start, end);
    if (deepLinkedEventId && page === 1 && !base.some(e => e.id === deepLinkedEventId)) {
      const match = allEvents.find(e => e.id === deepLinkedEventId);
      if (match) return [match, ...base].slice(0, pageSize + 1);
    }
    return base;
  };

  const getUpcomingEvents = (limit?: number): StaticEvent[] => {
    const now = new Date();
    const upcoming = allEvents.filter(event => event.date >= now);
    return limit ? upcoming.slice(0, limit) : upcoming;
  };

  const getEventsForDate = (date: Date): StaticEvent[] => {
    return allEvents.filter(event => {
      const eventDate = new Date(event.date);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const allUpcomingEvents = getUpcomingEvents();
  const upcomingEvents = allUpcomingEvents.slice(0, 20);
  const totalPages = Math.max(1, Math.ceil(allUpcomingEvents.length / pageSize));
  const listViewEvents = getListViewEvents();
  const eventsForSelectedDate = selectedDate ? getEventsForDate(selectedDate) : [];

  const renderPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, 4, 'ellipsis', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, 'ellipsis', page - 1, page, page + 1, 'ellipsis', totalPages);
    }
    return pages.map((p, i) => (
      p === 'ellipsis' ? (
        <PaginationItem key={`ellipsis-${i}`}>
          <PaginationEllipsis />
        </PaginationItem>
      ) : (
        <PaginationItem key={p}>
          <PaginationLink
            href="#"
            isActive={page === p}
            onClick={(e) => { e.preventDefault(); setPage(p); }}
          >
            {p}
          </PaginationLink>
        </PaginationItem>
      )
    ));
  };

  // Get all dates that have events
  const eventDates = allEvents.map(event => {
    const date = new Date(event.date);
    return format(date, "yyyy-MM-dd");
  });

  const hasEventOnDate = (date: Date) => {
    return eventDates.includes(format(date, "yyyy-MM-dd"));
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 animate-fade-in">
              {chapterName} Chapter Events
            </h1>
            <p className="text-lg text-muted-foreground animate-fade-in" style={{ animationDelay: "0.1s" }}>
              Join us for our upcoming events designed to foster connections and collaboration among businesses. Expand your network and gain valuable insights through engaging workshops and meetings.
            </p>
          </div>
        </div>
      </section>


      {/* Events Calendar and List */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="list" className="space-y-8">
            <div className="flex justify-center">
              <TabsList>
                <TabsTrigger value="list" className="gap-2">
                  <List className="w-4 h-4" />
                  List View
                </TabsTrigger>
                <TabsTrigger value="calendar" className="gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Calendar View
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="list" className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Upcoming Events</h2>
                <p className="text-muted-foreground">
                  View all upcoming networking meetings and special events
                </p>
              </div>
              
              {loading || error ? (
                renderStatusCard()
              ) : listViewEvents.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {listViewEvents.map((event) => (
                    <div key={event.id} id={`event-${event.id}`} className="animate-fade-in">
                      <EventCard event={event} autoOpenDetails={event.id === autoOpenDetailsId} />
                    </div>
                  ))}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-xl font-semibold mb-2">No Upcoming Events</h3>
                  <p className="text-muted-foreground">Check back soon for new events!</p>
                </Card>
              )}
              {!loading && !error && totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      {isMobile ? (
                        <PaginationLink
                          href="#"
                          onClick={(e) => { e.preventDefault(); setPage(p => Math.max(1, p - 1)); }}
                          aria-label="Go to previous page"
                          aria-disabled={page === 1}
                          className={page === 1 ? "pointer-events-none opacity-50" : ""}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </PaginationLink>
                      ) : (
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => { e.preventDefault(); setPage(p => Math.max(1, p - 1)); }}
                          aria-disabled={page === 1}
                          className={page === 1 ? "pointer-events-none opacity-50" : ""}
                        />
                      )}
                    </PaginationItem>
                    {renderPageNumbers()}
                    <PaginationItem>
                      {isMobile ? (
                        <PaginationLink
                          href="#"
                          onClick={(e) => { e.preventDefault(); setPage(p => Math.min(totalPages, p + 1)); }}
                          aria-label="Go to next page"
                          aria-disabled={page === totalPages}
                          className={page === totalPages ? "pointer-events-none opacity-50" : ""}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </PaginationLink>
                      ) : (
                        <PaginationNext
                          href="#"
                          onClick={(e) => { e.preventDefault(); setPage(p => Math.min(totalPages, p + 1)); }}
                          aria-disabled={page === totalPages}
                          className={page === totalPages ? "pointer-events-none opacity-50" : ""}
                        />
                      )}
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </TabsContent>

            <TabsContent value="calendar" className="space-y-6">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Calendar */}
                <div className="lg:col-span-1">
                  <Card className="p-6">
                    <h3 className="font-semibold mb-4">Select a Date</h3>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className={cn("rounded-md border pointer-events-auto")}
                      modifiers={{
                        hasEvent: (date) => hasEventOnDate(date)
                      }}
                      modifiersStyles={{
                        hasEvent: {
                          fontWeight: "bold",
                          backgroundColor: "hsl(var(--primary) / 0.1)",
                          color: "hsl(var(--primary))"
                        }
                      }}
                    />
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold text-primary">Bold dates</span> have scheduled events
                      </p>
                    </div>
                  </Card>
                </div>

                {/* Events for Selected Date */}
                <div className="lg:col-span-2">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">
                      {selectedDate ? format(selectedDate, "EEEE, MMMM d, yyyy") : "Select a Date"}
                    </h3>
                    {eventsForSelectedDate.length > 0 && (
                      <Badge variant="secondary">
                        {eventsForSelectedDate.length} {eventsForSelectedDate.length === 1 ? "Event" : "Events"}
                      </Badge>
                    )}
                  </div>

                  {loading || error ? (
                    renderStatusCard()
                  ) : eventsForSelectedDate.length > 0 ? (
                    <div className="space-y-4">
                      {eventsForSelectedDate.map((event) => (
                        <div key={event.id} id={`event-${event.id}`} className="animate-fade-in">
                          <EventCard event={event} autoOpenDetails={event.id === autoOpenDetailsId} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Card className="p-12 text-center">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h4 className="text-lg font-semibold mb-2">No Events This Day</h4>
                      <p className="text-muted-foreground mb-4">
                        Select a different date to view scheduled events
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const nextEvent = upcomingEvents[0];
                          if (nextEvent) {
                            setSelectedDate(nextEvent.date);
                          }
                        }}
                      >
                        Jump to Next Event
                      </Button>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Past Events CTA */}
      <section className="py-12 bg-secondary/30">
        <div className="container mx-auto px-4">
          <Card className="p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              For Highlights from Our Past Events
            </h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Explore the memorable moments from our past networking events. These gatherings have been instrumental in fostering strong connections and collaborative opportunities among our members.
            </p>
            <Button size="lg" asChild>
              <a
                href="https://www.facebook.com/profile.php?id=61566386020729"
                target="_blank"
                rel="noopener noreferrer"
              >
                Check Out Our Facebook Page
              </a>
            </Button>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Events;
